import { useEffect, useRef, useState, useCallback } from "react";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useMerchant } from "@auth";
import { resolveDateFilterValue } from "@utils";

// ── Client-side filter predicate ──────────────────────────────────────
// Pure function: takes a filters array and returns a predicate (item) => boolean.
// Used by ClientTable for client-side filtering; not needed by server-filtered tables.

const normalizeToDay = (d) => {
  const temp = new Date(d);
  temp.setHours(0, 0, 0, 0);
  return temp;
};

// Filter bounds are "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm". A bare date must be read as local
// midnight: `new Date("2026-10-22")` is UTC midnight, which west of Greenwich is still the
// previous local day. Date-times without an offset already parse as local.
const parseFilterDate = (value) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(...value.split("-").map((n, i) => Number(n) - (i === 1 ? 1 : 0)))
    : new Date(value);

export const buildFilterPredicate = (filters) => {
  return (item) => {
    return filters.every((f) => {
      const { type, key, value } = f;

      switch (type) {
        case "string":
          if (!value) return true;
          return (item[key] || "")
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase());

        case "enum":
          if (!Array.isArray(value) || value.length === 0) return true;
          return value.includes(item[key]);

        case "includeExcludeEnum": {
          const chains = Array.isArray(value) ? value : [];
          if (chains.length === 0) return true;
          const itemValues = Array.isArray(item[key]) ? item[key] : [item[key]];
          return chains.every((chain) => {
            if (!chain.selected || chain.selected.length === 0) return true;
            switch (chain.mode) {
              case "include":
                // OR within chain: item must match at least one selected value
                return chain.selected.some((v) => itemValues.includes(v));
              case "exclude":
                // Item must NOT match any selected value
                return !chain.selected.some((v) => itemValues.includes(v));
              default:
                return true;
            }
          });
        }

        case "number": {
          const { operator, from, to } = value || {};
          if (!operator) return true;

          const itemValue = Number(item[key]);
          if (isNaN(itemValue)) return true;

          switch (operator) {
            case "lessThan":
              return to === "" || isNaN(to) || itemValue < Number(to);
            case "greaterThan":
              return from === "" || isNaN(from) || itemValue > Number(from);
            case "between":
              return (
                (from === "" || isNaN(from) || itemValue >= Number(from)) &&
                (to === "" || isNaN(to) || itemValue <= Number(to))
              );
            default:
              return true;
          }
        }

        case "date": {
          const { operator, from, to } = resolveDateFilterValue(value) || {};
          if (!operator) return true;

          const itemValue = item[key];
          if (!itemValue) return false;

          const itemDate = new Date(itemValue);
          if (isNaN(itemDate)) return false;

          const hasTime = (v) => v && v.includes("T");

          switch (operator) {
            case "is": {
              if (!from) return true;
              const fromDate = parseFilterDate(from);
              if (isNaN(fromDate)) return true;
              return normalizeToDay(itemDate).getTime() === normalizeToDay(fromDate).getTime();
            }
            case "before": {
              if (!to) return true;
              const toDate = parseFilterDate(to);
              if (isNaN(toDate)) return true;
              if (hasTime(to)) return itemDate.getTime() <= toDate.getTime();
              return normalizeToDay(itemDate).getTime() <= normalizeToDay(toDate).getTime();
            }
            case "after": {
              if (!from) return true;
              const fromDate = parseFilterDate(from);
              if (isNaN(fromDate)) return true;
              if (hasTime(from)) return itemDate.getTime() >= fromDate.getTime();
              return normalizeToDay(itemDate).getTime() >= normalizeToDay(fromDate).getTime();
            }
            case "between": {
              if (!from || !to) return true;
              const fromDate = parseFilterDate(from);
              const toDate = parseFilterDate(to);
              if (isNaN(fromDate) || isNaN(toDate)) return true;
              const itemTs = hasTime(from) || hasTime(to) ? itemDate.getTime() : normalizeToDay(itemDate).getTime();
              const fromTs = hasTime(from) ? fromDate.getTime() : normalizeToDay(fromDate).getTime();
              const toTs = hasTime(to) ? toDate.getTime() : normalizeToDay(toDate).getTime();
              return itemTs >= fromTs && itemTs <= toTs;
            }
            default:
              return true;
          }
        }

        default:
          return true;
      }
    });
  };
};

// ── Default filter values ────────────────────────────────────────────

const getDefaultValue = (type) => {
  switch (type) {
    case "string":
      return "";
    case "enum":
      return [];
    case "includeExcludeEnum":
      return [{ mode: "include", selected: [] }];
    case "date":
      return { from: "", to: "", operator: "is" };
    case "number":
      return { from: "", to: "", operator: "greaterThan" };
    default:
      return "";
  }
};

/**
 * Centralised hook for table filter state, CRUD operations,
 * localStorage persistence, and auto-open behaviour.
 *
 * @param {string} configKey   - Table config key (e.g. "tradeAccounts").
 *                               Drives localStorage key and getColTitle override lookups.
 * @param {Array}  columns     - Column definitions array (needs .key, .type, .title, .icon, .values).
 * @param {Object} [options]
 * @param {boolean} [options.persistEnabled=true] - Set false to skip localStorage writes
 *                                                  (e.g. during recommendation flows).
 * @param {string}  [options.scope]               - Optional scope suffix for the storage key
 *                                                  (e.g. a groupId) so filters persist separately.
 */
const useTableFilters = (
  configKey,
  columns,
  { persistEnabled = true, scope } = {},
) => {
  const { merchant } = useMerchant();

  // ── Persistence ──────────────────────────────────────────────────────
  const storageKey = scope
    ? `filters-${configKey.toLowerCase()}-${scope}`
    : `filters-${configKey.toLowerCase()}`;
  const [savedFilters, setSavedFilters] = useLocalStorage(storageKey, {
    filters: [],
  });

  // ── State ────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState(savedFilters?.filters ?? []);
  const [autoOpenKey, setAutoOpenKey] = useState(null);
  const newFilterButtonRef = useRef(null);

  // Re-sync filter state when the storage key changes (e.g. navigating between groups).
  // Done during render (not in useEffect) so filters are correct before any
  // effects that depend on them fire — avoids a fetch with stale filters.
  const prevStorageKeyRef = useRef(storageKey);
  if (prevStorageKeyRef.current !== storageKey) {
    prevStorageKeyRef.current = storageKey;
    setFilters(savedFilters?.filters ?? []);
  }

  // Persist filters to localStorage (skipped when persistEnabled is false)
  useEffect(() => {
    if (!persistEnabled) return;
    setSavedFilters({ filters, saveDate: new Date() });
  }, [filters, persistEnabled]);

  // Validate persisted enum filter values against current column definitions.
  // When the merchant changes, column enum values are refetched for the new
  // merchant. Any persisted filter selections whose IDs/values no longer exist
  // (e.g. Groups, Branches, Salespeople) are stripped automatically.
  useEffect(() => {
    if (!columns?.length) return;

    const getValidValueSet = (colValues) => {
      if (!Array.isArray(colValues) || colValues.length === 0) return null;
      return new Set(
        colValues.map((v) => (typeof v === "object" ? v.value : v)),
      );
    };

    setFilters((prev) => {
      if (!prev.length) return prev;

      let changed = false;
      const cleaned = [];

      for (const filter of prev) {
        const col = columns.find((c) => c.key === filter.key);
        const validSet = col ? getValidValueSet(col.values) : null;

        if (!validSet) {
          cleaned.push(filter);
          continue;
        }

        if (
          filter.type === "enum" &&
          Array.isArray(filter.value) &&
          filter.value.length > 0
        ) {
          const kept = filter.value.filter((v) => validSet.has(v));
          if (kept.length !== filter.value.length) {
            changed = true;
            if (kept.length > 0) cleaned.push({ ...filter, value: kept });
            continue;
          }
        }

        if (
          filter.type === "includeExcludeEnum" &&
          Array.isArray(filter.value)
        ) {
          let chainChanged = false;
          const cleanedChains = filter.value.map((chain) => {
            if (!chain.selected?.length) return chain;
            const kept = chain.selected.filter((v) => validSet.has(v));
            if (kept.length !== chain.selected.length) {
              chainChanged = true;
              return { ...chain, selected: kept };
            }
            return chain;
          });

          if (chainChanged) {
            changed = true;
            const hasAny = cleanedChains.some((c) => c.selected?.length > 0);
            if (hasAny) cleaned.push({ ...filter, value: cleanedChains });
            continue;
          }
        }

        cleaned.push(filter);
      }

      return changed ? cleaned : prev;
    });
  }, [columns]);

  // Auto-open a newly added filter's editor dropdown
  useEffect(() => {
    if (autoOpenKey && newFilterButtonRef.current) {
      newFilterButtonRef.current.click();
      setAutoOpenKey(null);
    }
  }, [autoOpenKey]);

  // ── getColTitle ──────────────────────────────────────────────────────
  const getColTitle = useCallback(
    (col) => {
      const overrides = merchant?.customisationSettings?.columnOverrides;
      if (overrides) {
        const match = overrides.find(
          (x) => x.key === `${configKey}.${col.key}`,
        );
        if (match) return match.value;
      }
      return col.title;
    },
    [merchant, configKey],
  );

  // ── CRUD ─────────────────────────────────────────────────────────────

  const addFilter = useCallback(
    (key) => {
      const col = columns.find((x) => x.key === key);
      if (!col) return;

      setFilters((prev) => [
        ...prev,
        {
          key: col.key,
          title: getColTitle(col),
          type: col.type,
          icon: col.icon,
          value: getDefaultValue(col.type),
          values: col.values,
        },
      ]);
      setAutoOpenKey(key);
    },
    [columns, getColTitle],
  );

  const updateFilter = useCallback(
    (key, newValue) => {
      setFilters((prev) => {
        const idx = prev.findIndex((f) => f.key === key);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], value: newValue };
          return next;
        }
        // Add new filter if it doesn't exist yet
        const col = columns.find((x) => x.key === key);
        if (!col) return prev;
        return [
          ...prev,
          {
            key: col.key,
            type: col.type,
            title: getColTitle(col),
            icon: col.icon,
            value: newValue,
          },
        ];
      });
    },
    [columns, getColTitle],
  );

  const removeFilter = useCallback((key) => {
    setFilters((prev) => prev.filter((f) => f.key !== key));
  }, []);

  // Restore the last persisted filters (useful after recommendation flows)
  const restoreFilters = useCallback(() => {
    setFilters(savedFilters?.filters || []);
  }, [savedFilters]);

  return {
    filters,
    setFilters,
    addFilter,
    updateFilter,
    removeFilter,
    autoOpenKey,
    newFilterButtonRef,
    getColTitle,
    restoreFilters,
    scope,
  };
};

export default useTableFilters;
